import React, { useState, useEffect } from 'react';
import { getTeamMembers, createTeamMember, updateTeamMember, deleteTeamMember, toggleTeamStatus, getCategories } from '../../services/api';
import DataTable from '../components/DataTable';
import LoadingSpinner from '../components/LoadingSpinner';
import TeamForm from '../forms/TeamForm';
import toast from 'react-hot-toast';

const TeamModule = () => {
  const [team, setTeam] = useState([]);
  const [teamCategories, setTeamCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const columns = ['Photo', 'Name', 'Position', 'Department', 'Status', 'Actions'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [teamResponse, categoriesResponse] = await Promise.all([
        getTeamMembers(),
        getCategories()
      ]);
      
      // Process team members
      let teamData = teamResponse.data || teamResponse || [];
      
      // Ensure each team member has a department
      teamData = teamData.map(member => ({
        ...member,
        department: member.department || 'General'
      }));
      
      setTeam(teamData);
      
      // Process categories - filter only team type
      let categoriesData = [];
      if (categoriesResponse && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
        categoriesData = categoriesResponse.data.filter(cat => cat.type === 'team');
      } else if (categoriesResponse && Array.isArray(categoriesResponse)) {
        categoriesData = categoriesResponse.filter(cat => cat.type === 'team');
      }
      
      setTeamCategories(categoriesData);
      
      console.log('Team members loaded:', teamData.length);
      console.log('Team categories loaded:', categoriesData.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error fetching team members');
      setTeam([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        await deleteTeamMember(id);
        toast.success('Team member deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Error deleting team member');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleTeamStatus(id);
      toast.success(`Team member ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Error toggling team member status');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  // Updated filtering logic - show all when 'all' is selected
  const filteredTeam = selectedCategory === 'all'
    ? team
    : team.filter(member => member.department === selectedCategory);

  if (loading) {
    return <LoadingSpinner message="Loading team members..." />;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#0A192F', marginBottom: '0.5rem' }}>Team Management</h2>
        <p style={{ color: '#5A6E7F' }}>Manage your organization's team members</p>
      </div>

      {/* Category Filter Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: '#F7F9FC',
        borderRadius: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500, color: '#0A192F' }}>
            <i className="fas fa-filter"></i> Filter by Department:
          </span>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              background: 'white',
              fontSize: '0.875rem',
              minWidth: '180px'
            }}
          >
            <option value="all">📋 All Departments ({team.length})</option>
            {teamCategories.map((cat) => {
              const count = team.filter(m => m.department === cat.name).length;
              return (
                <option key={cat._id} value={cat.name}>
                  {cat.icon && <span style={{ marginRight: '0.5rem' }}>{cat.icon}</span>}
                  {cat.name} ({count})
                </option>
              );
            })}
          </select>
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setViewMode('grid')}
            style={{
              padding: '0.5rem 1rem',
              background: viewMode === 'grid' ? '#2C7A7B' : '#FFFFFF',
              color: viewMode === 'grid' ? 'white' : '#5A6E7F',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <i className="fas fa-th-large"></i> Grid View
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: '0.5rem 1rem',
              background: viewMode === 'table' ? '#2C7A7B' : '#FFFFFF',
              color: viewMode === 'table' ? 'white' : '#5A6E7F',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <i className="fas fa-table"></i> Table View
          </button>
        </div>
      </div>

      {/* Add Member Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
        <button 
          onClick={() => { setEditingItem(null); setShowModal(true); }} 
          style={{ 
            padding: '0.6rem 1.2rem', 
            background: '#2C7A7B', 
            color: 'white', 
            border: 'none', 
            borderRadius: '10px', 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontWeight: 500,
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#2C7A7B'}
        >
          <i className="fas fa-plus"></i> Add New Member
        </button>
      </div>

      {/* Stats */}
      <div style={{ marginBottom: '1.5rem' }}>
        <p style={{ color: '#5A6E7F', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="fas fa-users" style={{ color: '#2C7A7B' }}></i>
          <strong>{filteredTeam.length}</strong> {filteredTeam.length === 1 ? 'team member' : 'team members'} found
          {selectedCategory !== 'all' && <span> in <strong>{selectedCategory}</strong></span>}
        </p>
      </div>

      {filteredTeam.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: '#F7F9FC', borderRadius: '20px', border: '2px dashed #E2E8F0' }}>
          <i className="fas fa-users" style={{ fontSize: '4rem', color: '#CBD5E1', marginBottom: '1rem' }}></i>
          <p style={{ color: '#5A6E7F', marginBottom: '1rem', fontSize: '1.1rem' }}>No team members found</p>
          <button onClick={() => { setEditingItem(null); setShowModal(true); }} style={{ padding: '0.6rem 1.2rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 500 }}>
            <i className="fas fa-plus"></i> Add Your First Team Member
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredTeam.map((member) => (
            <TeamCard
              key={member._id}
              member={member}
              getFullImageUrl={getFullImageUrl}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} style={{ padding: '1rem', textAlign: 'left' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTeam.map((member) => (
                <tr key={member._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem' }}>
                    {member.photo ? (
                      <img src={getFullImageUrl(member.photo)} alt={member.name} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '50%' }} />
                    ) : (
                      <div style={{ width: '40px', height: '40px', background: '#2C7A7B', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                        {member.name?.charAt(0) || 'U'}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{member.name}</td>
                  <td style={{ padding: '1rem' }}>{member.position}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      padding: '0.2rem 0.6rem',
                      background: '#E3F2FD',
                      borderRadius: '20px',
                      fontSize: '0.7rem'
                    }}>
                      {member.department}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleToggle(member._id, member.isActive)} 
                      style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none', background: member.isActive ? '#D4EDDA' : '#F8D7DA', color: member.isActive ? '#155724' : '#721C24', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      {member.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleEdit(member)} 
                      style={{ padding: '0.35rem 0.8rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem', fontSize: '0.75rem' }}
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(member._id)} 
                      style={{ padding: '0.35rem 0.8rem', background: '#DC2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TeamForm 
          editingItem={editingItem} 
          onClose={handleCloseModal} 
          onRefresh={fetchData}
          teamCategories={teamCategories}
        />
      )}
    </div>
  );
};

// Team Card Component
const TeamCard = ({ member, getFullImageUrl, onEdit, onDelete, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        border: '1px solid #E2E8F0',
        position: 'relative',
        textAlign: 'center'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Status Badge */}
      <div style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 1 }}>
        <button
          onClick={() => onToggle(member._id, member.isActive)}
          style={{
            padding: '0.3rem 0.8rem',
            borderRadius: '20px',
            border: 'none',
            background: member.isActive ? '#10B981' : '#EF4444',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.7rem',
            fontWeight: 600
          }}
        >
          {member.isActive ? 'Active' : 'Inactive'}
        </button>
      </div>

      <div style={{ padding: '1.5rem' }}>
        {/* Photo */}
        {member.photo ? (
          <img 
            src={getFullImageUrl(member.photo)} 
            alt={member.name} 
            style={{ 
              width: '120px', 
              height: '120px', 
              borderRadius: '50%', 
              objectFit: 'cover',
              margin: '0 auto 1rem',
              display: 'block',
              border: '3px solid #E2E8F0'
            }} 
          />
        ) : (
          <div style={{ 
            width: '120px', 
            height: '120px', 
            background: 'linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%)', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: 'white',
            fontSize: '3rem',
            fontWeight: 'bold',
            margin: '0 auto 1rem'
          }}>
            {member.name?.charAt(0) || 'U'}
          </div>
        )}

        {/* Name and Position */}
        <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#0A192F', marginBottom: '0.25rem' }}>
          {member.name}
        </h3>
        <p style={{ fontSize: '0.85rem', color: '#2C7A7B', fontWeight: 500, marginBottom: '0.5rem' }}>
          {member.position}
        </p>

        {/* Department Badge */}
        <div style={{ marginBottom: '1rem' }}>
          <span style={{
            background: '#E3F2FD',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.7rem',
            color: '#2C7A7B',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem'
          }}>
            <i className="fas fa-building"></i> {member.department}
          </span>
        </div>

        {/* Bio Preview */}
        {member.bio && (
          <p style={{
            fontSize: '0.8rem',
            color: '#5A6E7F',
            lineHeight: '1.5',
            marginBottom: '1rem',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {member.bio.length > 100 ? `${member.bio.substring(0, 100)}...` : member.bio}
          </p>
        )}

        {/* Social Links */}
        {(member.linkedin || member.twitter || member.email) && (
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1rem' }}>
            {member.email && (
              <a href={`mailto:${member.email}`} style={{ color: '#5A6E7F', fontSize: '1rem' }}>
                <i className="fas fa-envelope"></i>
              </a>
            )}
            {member.linkedin && (
              <a href={member.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#5A6E7F', fontSize: '1rem' }}>
                <i className="fab fa-linkedin"></i>
              </a>
            )}
            {member.twitter && (
              <a href={member.twitter} target="_blank" rel="noopener noreferrer" style={{ color: '#5A6E7F', fontSize: '1rem' }}>
                <i className="fab fa-twitter"></i>
              </a>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => onEdit(member)}
            style={{
              flex: 1,
              padding: '0.5rem',
              background: '#2C7A7B',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
          >
            <i className="fas fa-edit"></i> Edit
          </button>
          <button
            onClick={() => onDelete(member._id)}
            style={{
              flex: 1,
              padding: '0.5rem',
              background: '#DC2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#B91C1C'}
          >
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamModule;